// ═══════════════════════════════════════════════════════════════════════
//  OcSelecao.jsx — montar e GERIR a carteira do Capital Aberto
//
//  O motor inteiro é do backend (services/oc/oc_watchlist.js). Esta tela fala
//  com cinco rotas e mais nada:
//
//    GET  /api/oc/carteira                estado do ciclo e do plano
//    GET  /api/oc/universo                o cardápio (553 abertas)
//    GET  /api/oc/setores                 filtro
//    POST /api/oc/carteira/selecao        { cnpjs: [...] }   — 1ª vez, trava o ciclo
//    POST /api/oc/carteira/troca          { remover, incluir } — depois disso
//    POST /api/oc/carteira/troca/simular  o mesmo corpo, sem gravar
//
//  TRÊS ESTADOS, NÃO DOIS. A versão anterior tinha só "montar" e "sumir": depois
//  de confirmar, a tela dava `return null` e o cliente ficava preso com o que
//  tinha escolhido. O Grupo Cesari confirmou com 4 de 40 e não conseguiu incluir
//  mais nenhuma — 36 vagas pagas e inalcançáveis. Os três estados agora são:
//
//    sem contrato   → não renderiza nada (a aba é da carteira monitorada)
//    seleção aberta → montagem: escolher até o limite e confirmar
//    confirmada     → gestão: uma faixa fina + o quadro, sob demanda
//
//  AS DUAS OPERAÇÕES DEPOIS DE CONFIRMADA SÃO DIFERENTES, e a tela as separa
//  porque o contrato as cobra diferente — medido em produção, não suposto:
//
//    remover: [] + incluir: [...]  → tipo 'slot_vago', consome_janela: false
//    remover: [x] + incluir: [...] → tipo 'troca',     consome_janela: true
//
//  Preencher vaga livre é de graça e pode ser feito quantas vezes quiser, até o
//  limite do plano. Substituir abre a JANELA ÚNICA da vigência: uma vez usada,
//  só um admin da FinancialMind reabre. Por isso o quadro de substituições é
//  deliberadamente todo-de-uma-vez e avisa antes — quem trocar uma empresa hoje
//  e quiser trocar outra amanhã levará `janela_troca_ja_utilizada` na cara.
//
//  CNPJ: o universo devolve mascarado (05.303.439/0001-07) e a carteira devolve
//  só dígitos (05303439000107). Foi assim que o carrinho passou a mostrar o CNPJ
//  cru e "— · PD —" no lugar do nome: `Map.get` com a chave de um formato e as
//  chaves gravadas no outro. Tudo aqui é chaveado por `dig()` — dígitos — e o
//  formato bonito só aparece na renderização.
//
//  NÃO HÁ PD NO CARDÁPIO, E ISSO É DECISÃO COMERCIAL — NÃO É BUG (18/09/2026)
//  ────────────────────────────────────────────────────────────────────────
//  A PD é o produto. Quem tem login de um pacote de 40 não pode ler a PD das
//  outras 493 só por abrir esta tela — seria entregar de graça o que o cliente
//  contrata. Desde a v3.48.0 o servidor mascara: /api/oc/universo devolve
//  `pd: null` para quem está FORA da carteira (services/oc/oc_carteira_escopo.js,
//  semPdNoCatalogo) e a PD real só para quem está DENTRO.
//
//  Esta tela é quase toda "fora da carteira" por definição — `baseDeEscolha` é
//  o universo MENOS quem já é monitorado. Manter uma coluna "PD" aqui produzia
//  533 travessões e uma ordenação "Maior PD primeiro" que não ordenava nada:
//  foi exatamente o que subiu em produção com a v3.48.0, porque quem escreveu
//  a máscara procurou por `pd_final|pd_pct|fmtProb` e não pelo campo cru `pd`.
//
//  Então: o cardápio ordena e informa por RATING e PERCENTIL SETORIAL, que são
//  os dois sinais que o cliente já tinha nesta tela e que não são o produto. A
//  PD aparece em DOIS lugares, ambos dentro da carteira dele:
//
//    · o quadro de substituição (quem sai) — são empresas dele, PD real;
//    · as abas de monitoramento, depois que a empresa entra no pacote.
//
//  O carrinho ("Quem entra") NÃO mostra PD: quem está no carrinho ainda não é
//  do cliente, e imprimir `pct(e.pd)` ali só produziria travessão.
//
//  SE ALGUÉM FOR "CONSERTAR" A COLUNA QUE FALTA: o que falta não é a coluna, é
//  o contrato. Mudar isto é mudar o que o pacote vende, e essa decisão não é
//  de quem edita este arquivo.
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useMemo, useCallback } from "react";

const API_DEFAULT = "https://api.financialmind.ai";

const C = {
  navy: "#0A1628",
  blue: "#1A3F6F",
  amber: "#C87B2A",
  gold: "#E8C56D",
  cream: "#FAF7F2",
  red: "#DC2626",
  green: "#15803D",
  amber2: "#D97706",
  muted: "#6b7280",
  line: "#e5e7eb",
  ink: "#0f172a",
  paper: "#ffffff",
  bg: "#f8fafc",
};
const FONT = "Inter, system-ui, -apple-system, Segoe UI, Roboto, sans-serif";

/* A escala do RiskMind não é escala de agência — é interna, e a ordem abaixo é
   só para ordenar a lista. Não traduzir para Moody's/S&P em lugar nenhum. */
const ORDEM_RATING = ["AAA", "AA", "A", "BBB", "BB", "B", "CCC", "CC", "C", "D"];
const postoRating = (r) => {
  const i = ORDEM_RATING.indexOf(String(r || "").toUpperCase().trim());
  return i < 0 ? ORDEM_RATING.length : i;
};

/* Chave canônica. Uma linha, mas é a linha que faltava: o universo vem
   mascarado do `oc_snapshot` e a carteira vem em dígitos do `oc_watchlist_item`.
   Comparar os dois na forma bruta não casa nunca — e não casar em silêncio é o
   pior defeito possível, porque a tela desenha tudo, só que vazio. */
const dig = (c) => String(c == null ? "" : c).replace(/\D/g, "");

/* As mensagens vêm do servidor por código. Traduzir aqui, num lugar só, evita
   que a tela invente explicação diferente da regra que foi de fato aplicada. */
const MOTIVOS = {
  sem_ciclo:
    "Não há ciclo aberto para este contrato. Fale com a FinancialMind — o pacote precisa ser concedido antes da seleção.",
  selecao_ja_confirmada:
    "Esta carteira já foi confirmada neste ciclo. Use o quadro de inclusão ou o de substituição.",
  vigencia_expirada:
    "A vigência do contrato terminou. A carteira não pode ser alterada até a renovação.",
  selecao_vazia: "Escolha ao menos uma empresa antes de confirmar.",
  selecao_pendente:
    "A seleção inicial ainda não foi confirmada. Confirme a carteira antes de incluir ou substituir.",
  limite_excedido: "Você escolheu mais empresas do que o plano permite.",
  cnpj_fora_do_universo:
    "Alguma empresa escolhida saiu do universo desde que a lista foi carregada. Recarregue e tente de novo.",
  pedido_vazio: "Marque ao menos uma empresa antes de enviar.",
  remover_nao_monitorada:
    "Uma das empresas marcadas para sair não está na carteira. Recarregue a página.",
  incluir_ja_monitorada:
    "Uma das empresas marcadas para entrar já está na carteira.",
  janela_troca_ja_utilizada:
    "A janela de trocas desta vigência já foi usada. Preencher vagas livres continua liberado; para trocar de novo, fale com a FinancialMind.",
  limite_troca_excedido:
    "Você marcou mais saídas do que as trocas contratadas para esta vigência.",
  substituicao_desbalanceada:
    "A substituição involuntária precisa de uma entrada para cada saída.",
  empresa_ainda_no_universo:
    "Essa empresa continua no universo — a saída dela é troca comum, não substituição involuntária.",
  confirmacao_requerida: "Falta confirmar a operação.",
  apenas_admin_define_carteira:
    "Definir a carteira é de Admin, Admin CFO ou CFO/Diretor. Você segue com acesso de leitura.",
  papel_ausente: "Não foi possível identificar seu perfil. Saia e entre de novo.",
  falha_ao_verificar_papel:
    "Não foi possível verificar seu perfil agora. Tente de novo em instantes.",
  oc_entitlement_required:
    "Este login não tem o Capital Aberto liberado. Fale com o admin da sua empresa.",
};
const explicar = (j, status) => {
  const cod = (j && (j.erro || j.error)) || null;
  const base = (cod && MOTIVOS[cod]) || null;
  if (base && cod === "limite_excedido" && j && j.limite != null) {
    return `${base} Limite do plano: ${j.limite}. Resultado do pedido: ${j.resultado != null ? j.resultado : j.enviados}.`;
  }
  if (base && cod === "limite_troca_excedido" && j && j.trocas_permitidas != null) {
    return `${base} Contratadas: ${j.trocas_permitidas}. Solicitadas: ${j.solicitadas}.`;
  }
  if (base) return base;
  if (cod) return `O servidor recusou: ${cod}.`;
  return `Não consegui gravar (HTTP ${status}). Tente de novo.`;
};

const pct = (v, d = 1) =>
  v == null || Number.isNaN(Number(v)) ? "—" : (Number(v) * 100).toFixed(d) + "%";
const fmtCnpj = (c) => {
  const s = dig(c);
  if (s.length !== 14) return c || "—";
  return `${s.slice(0, 2)}.${s.slice(2, 5)}.${s.slice(5, 8)}/${s.slice(8, 12)}-${s.slice(12)}`;
};
/* O `sector_pct` do /api/oc/universo vem em 0–100, não em fração — eu supus
   fração, multipliquei por 100 e a tela mostrou "10000º". Aceitar as duas
   escalas custa uma linha e evita que a próxima mudança de unidade no backend
   produza outro número absurdo em silêncio. */
const fmtPercentil = (v) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const n = Number(v);
  const p = n > 1 ? n : n * 100;
  return `${Math.round(p)}º`;
};
const fmtData = (d) => {
  if (!d) return "—";
  const s = String(d).slice(0, 10).split("-");
  return s.length === 3 ? `${s[2]}/${s[1]}/${s[0]}` : String(d);
};
const plural = (n, um, muitos) => `${n} ${n === 1 ? um : muitos}`;

const Btn = ({ label, onClick, disabled, tom = "principal", pequeno, style, title }) => {
  const tons = {
    principal: { background: C.blue, color: "#fff", border: `1px solid ${C.blue}` },
    ouro: { background: C.amber, color: "#fff", border: `1px solid ${C.amber}` },
    plano: { background: C.paper, color: C.ink, border: `1px solid ${C.line}` },
    perigo: { background: C.paper, color: C.red, border: `1px solid ${C.red}` },
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        ...tons[tom],
        fontFamily: FONT,
        fontSize: pequeno ? 12 : 13,
        fontWeight: 600,
        padding: pequeno ? "6px 10px" : "9px 16px",
        borderRadius: 8,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.45 : 1,
        transition: "opacity .15s",
        ...style,
      }}
    >
      {label}
    </button>
  );
};

const Rotulo = ({ children, style }) => (
  <div
    style={{
      fontFamily: "'JetBrains Mono', ui-monospace, SFMono-Regular, monospace",
      fontSize: 10,
      letterSpacing: ".11em",
      textTransform: "uppercase",
      color: C.amber,
      marginBottom: 8,
      ...style,
    }}
  >
    {children}
  </div>
);

const campoStyle = {
  fontFamily: FONT,
  fontSize: 13,
  padding: "8px 11px",
  borderRadius: 8,
  border: `1px solid ${C.line}`,
  background: C.paper,
  color: C.ink,
  outline: "none",
};

const Cartao = ({ k, v, tom }) => (
  <div
    style={{
      background: C.paper,
      border: `1px solid ${C.line}`,
      borderRadius: 10,
      padding: "10px 14px",
      minWidth: 128,
    }}
  >
    <div style={{ fontSize: 11, color: C.muted, marginBottom: 3 }}>{k}</div>
    <div style={{ fontSize: 15, fontWeight: 650, color: tom || C.ink }}>{v}</div>
  </div>
);

function Nota({ cor, children }) {
  return (
    <div
      style={{
        background: C.paper,
        border: `1px solid ${C.line}`,
        borderLeft: `3px solid ${cor}`,
        borderRadius: 8,
        padding: "11px 14px",
        fontSize: 13,
        color: C.ink,
        lineHeight: 1.55,
        marginBottom: 16,
      }}
    >
      {children}
    </div>
  );
}

/* O token pode nao vir por prop: o OcApp e montado em dois lugares e nenhum
   dos dois passava token ate hoje. Ele proprio se vira lendo o localStorage,
   entao esta tela faz o mesmo — e na ordem certa: a chave que o app realmente
   grava (`riskmind_token`) vem primeiro. As outras tres ficam por compatibilidade
   com o que o OcApp ja tentava, e a varredura final e o mesmo ultimo recurso
   dele: procurar qualquer valor com forma de JWT. */
const resolverToken = (tokenDeProps) => {
  if (tokenDeProps) return tokenDeProps;
  try {
    for (const k of ["riskmind_token", "token", "rm_token", "jwt"]) {
      const v = localStorage.getItem(k);
      if (v) return v;
    }
    for (let i = 0; i < localStorage.length; i++) {
      const v = localStorage.getItem(localStorage.key(i));
      if (v && /^eyJ[\w-]+\.[\w-]+\.[\w-]+$/.test(v)) return v;
    }
  } catch (e) {
    /* navegador com storage bloqueado — segue sem token e a tela avisa */
  }
  return null;
};

/* Sem `onAbrirFicha` de proposito. A ficha completa e o produto; esta tela e a
   vitrine de escolha. O portao de verdade e do servidor (routes/oc.js,
   exigeNaCarteira, v3.38.0): empresa fora da carteira devolve 403. Aqui a tela
   so evita oferecer o que o servidor vai negar. */
export default function OcSelecao({ token: tokenProp, apiBase = API_DEFAULT, onPronto }) {
  const token = resolverToken(tokenProp);
  const [carteira, setCarteira] = useState(null);
  const [universo, setUniverso] = useState([]);
  const [setores, setSetores] = useState([]);
  const [papel, setPapel] = useState(null);

  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState("");
  const [gravando, setGravando] = useState(false);
  const [aviso, setAviso] = useState(null); // { tipo: 'erro'|'ok', texto }

  /* Gestão só abre quando o usuário pede. Depois de confirmada, o dia a dia é a
     carteira monitorada logo abaixo; empurrar o quadro inteiro por cima dela
     toda vez seria repetir o erro que o fundador chamou de "bagunçou o que
     funcionava". */
  const [aberto, setAberto] = useState(false);
  const [modo, setModo] = useState("incluir"); // 'incluir' | 'substituir'

  const [escolhidas, setEscolhidas] = useState(() => new Set()); // montagem
  const [entrando, setEntrando] = useState(() => new Set());     // gestão: quem entra
  const [saindo, setSaindo] = useState(() => new Set());         // gestão: quem sai
  const [previa, setPrevia] = useState(null);                    // /troca/simular

  const [busca, setBusca] = useState("");
  const [setor, setSetor] = useState("");
  const [ordem, setOrdem] = useState("rating_pior");
  const [soComMercado, setSoComMercado] = useState(false);
  const [mostrar, setMostrar] = useState(60);

  const get = useCallback(
    async (caminho) => {
      const r = await fetch(apiBase + caminho, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!r.ok) throw new Error(caminho + " → HTTP " + r.status);
      return r.json();
    },
    [apiBase, token]
  );

  const post = useCallback(
    async (caminho, corpo) => {
      const r = await fetch(apiBase + caminho, {
        method: "POST",
        headers: {
          Authorization: "Bearer " + token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(corpo),
      });
      let j = null;
      try {
        j = await r.json();
      } catch (e) {
        j = null;
      }
      return { http: r.status, ok: r.ok && !(j && j.ok === false), corpo: j };
    },
    [apiBase, token]
  );

  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      setErroCarga("");
      try {
        /* O universo tem 553 linhas e o limite por página é 500. Duas chamadas
           trazem tudo, e a partir daí filtro e ordenação são locais: numa tela
           de escolher 40 entre centenas, o usuário troca de filtro o tempo
           todo, e ir ao servidor a cada tecla tornaria a tela pesada à toa. */
        const [c, s, p1] = await Promise.all([
          get("/api/oc/carteira"),
          get("/api/oc/setores"),
          get("/api/oc/universo?limit=500&offset=0"),
        ]);
        const linhas = (p1.rows || []).slice();
        if ((p1.total || 0) > linhas.length) {
          const p2 = await get(`/api/oc/universo?limit=500&offset=${linhas.length}`);
          linhas.push(...(p2.rows || []));
        }
        let quemSou = null;
        try {
          const m = await get("/api/auth/me");
          quemSou = (m && (m.user || m)) || null;
        } catch (e) {
          quemSou = null; // não é motivo para derrubar a tela
        }
        if (!vivo) return;
        setCarteira(c);
        setSetores(Array.isArray(s) ? s : []);
        setUniverso(linhas);
        setPapel(quemSou ? String(quemSou.role || "").trim().toLowerCase() : null);
        if (!c.selecao_confirmada_em) {
          setEscolhidas(new Set((c.empresas || []).map((e) => dig(e.cnpj))));
        }
      } catch (e) {
        if (vivo) setErroCarga(e && e.message ? e.message : String(e));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => {
      vivo = false;
    };
  }, [get]);

  const limite = (carteira && carteira.limite_empresas) || 0;
  const jaConfirmada = !!(carteira && carteira.selecao_confirmada_em);
  const monitoradas = useMemo(
    () => new Set(((carteira && carteira.empresas) || []).map((e) => dig(e.cnpj))),
    [carteira]
  );
  const vagas = Math.max(0, limite - monitoradas.size);
  const trocasRestantes = Math.max(
    0,
    ((carteira && carteira.trocas_permitidas) || 0) - ((carteira && carteira.trocas_usadas) || 0)
  );
  const janelaUsada = !!(carteira && carteira.janela_troca_usada);

  /* Espelha PAPEIS_QUE_DEFINEM_CARTEIRA do backend (oc_watchlist.js, v3.36.0).
     Se as duas listas divergirem, quem manda é o servidor: aqui a lista só
     evita que alguém monte uma carteira inteira e leve 403 no fim. */
  const podeDefinir = ["admin", "tenant_admin", "cfo"].includes(papel);

  const porCnpj = useMemo(() => {
    const m = new Map();
    for (const e of universo) m.set(dig(e.cnpj), e);
    return m;
  }, [universo]);

  const nomeDe = useCallback(
    (k) => {
      const e = porCnpj.get(dig(k));
      return (e && e.denom) || fmtCnpj(k);
    },
    [porCnpj]
  );

  /* A base do que é oferecido muda com o modo. Na montagem, o universo inteiro.
     Na inclusão e na substituição, o universo MENOS quem já está monitorado —
     oferecer uma empresa que já está na carteira só produz
     `incluir_ja_monitorada` do outro lado. */
  const baseDeEscolha = useMemo(() => {
    if (!jaConfirmada) return universo;
    return universo.filter((e) => !monitoradas.has(dig(e.cnpj)));
  }, [universo, jaConfirmada, monitoradas]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    const qDigitos = q.replace(/\D/g, "");
    const out = baseDeEscolha.filter((e) => {
      if (setor && e.setor !== setor) return false;
      if (soComMercado && !e.market_present) return false;
      if (!q) return true;
      const nome = String(e.denom || "").toLowerCase();
      const tick = String(e.ticker || "").toLowerCase();
      return (
        nome.includes(q) ||
        (tick && tick.includes(q)) ||
        (qDigitos.length >= 3 && dig(e.cnpj).includes(qDigitos))
      );
    });
    /* SEM ORDENAÇÃO POR PD — ver o cabeçalho. `e.pd` vem `null` para quem está
       fora da carteira, que é praticamente toda esta lista, e uma comparação
       `(b.pd||0)-(a.pd||0)` sobre nulos não reordena nada: o <select> continua
       respondendo ao clique e a lista fica parada. Ordenação que não ordena é
       pior que ausência de ordenação, porque o usuário confia nela.

       O PADRÃO É O PIOR RATING PRIMEIRO. Era o que "Maior PD primeiro" queria
       dizer: mostre primeiro quem vale a pena monitorar.

       DUAS ARMADILHAS, as duas já pagas neste repositório:

       1. `postoRating` devolve ORDEM_RATING.length para rating desconhecido —
          ou seja, PIOR que D. Ordenar por posto cru jogaria as sem-rating para
          o topo da lista de "piores". Não ter nota não é ter nota péssima, e o
          cliente escolheria 40 empresas sobre as quais não sabemos nada. Sem
          rating vai para o FIM nas duas direções, sempre.

       2. `sector_pct` desempata, mas só como CHAVE SECUNDÁRIA. Se um dia ele
          também for mascarado, a ordenação continua de pé pelo rating em vez
          de virar outra lista parada. Note o `!= null`: `Number(0) || -1` dá
          -1, e percentil 0 é um valor legítimo. */
    const DESCONHECIDO = ORDEM_RATING.length;
    const perc = (e) => (e && e.sector_pct != null && !Number.isNaN(Number(e.sector_pct)) ? Number(e.sector_pct) : null);
    const porPercentilDesc = (a, b) => {
      const pa = perc(a);
      const pb = perc(b);
      if (pa == null && pb == null) return 0;
      if (pa == null) return 1;
      if (pb == null) return -1;
      return pb - pa;
    };
    const porRating = (dir) => (a, b) => {
      const pa = postoRating(a.rating);
      const pb = postoRating(b.rating);
      const na = pa === DESCONHECIDO;
      const nb = pb === DESCONHECIDO;
      if (na !== nb) return na ? 1 : -1;
      if (pa !== pb) return dir === "pior" ? pb - pa : pa - pb;
      return porPercentilDesc(a, b);
    };
    const cmp = {
      rating_pior: porRating("pior"),
      rating_melhor: porRating("melhor"),
      nome: (a, b) => String(a.denom || "").localeCompare(String(b.denom || ""), "pt-BR"),
      setor: (a, b) =>
        String(a.setor || "").localeCompare(String(b.setor || ""), "pt-BR") ||
        String(a.denom || "").localeCompare(String(b.denom || ""), "pt-BR"),
    };
    return out.slice().sort(cmp[ordem] || cmp.rating_pior);
  }, [baseDeEscolha, busca, setor, soComMercado, ordem]);

  /* Teto de marcação por modo. Na montagem é o limite do plano. Na inclusão são
     as vagas livres. Na substituição, cada saída abre uma vaga — mais as que já
     estavam livres. O servidor cobra a mesma conta; aqui ela existe só para o
     usuário não descobrir o teto no erro. */
  const tetoEntrada = jaConfirmada
    ? modo === "incluir"
      ? vagas
      : vagas + saindo.size
    : limite;

  const marcarEntrada = (cnpj) => {
    setAviso(null);
    setPrevia(null);
    const k = dig(cnpj);
    const alvo = jaConfirmada ? setEntrando : setEscolhidas;
    alvo((atual) => {
      const nova = new Set(atual);
      if (nova.has(k)) nova.delete(k);
      else if (nova.size >= tetoEntrada) return atual; // o contador já explica
      else nova.add(k);
      return nova;
    });
  };

  const marcarSaida = (cnpj) => {
    setAviso(null);
    setPrevia(null);
    const k = dig(cnpj);
    const nova = new Set(saindo);
    if (nova.has(k)) nova.delete(k);
    else if (nova.size >= trocasRestantes) return; // o contador já explica
    else nova.add(k);
    setSaindo(nova);
    /* Tirar uma saída derruba o teto de entradas. Deixar as duas listas
       incoerentes só adiaria o erro para o servidor; corta o excedente aqui.
       Calculado fora do updater de propósito: chamar um setState dentro do
       updater de outro roda duas vezes em StrictMode. */
    const teto = vagas + nova.size;
    if (entrando.size > teto) setEntrando(new Set([...entrando].slice(0, teto)));
  };

  const recarregarCarteira = useCallback(async () => {
    try {
      const c = await get("/api/oc/carteira");
      setCarteira(c);
      if (onPronto) onPronto(c);
      return c;
    } catch (e) {
      return null;
    }
  }, [get, onPronto]);

  // ── montagem: grava a seleção inicial e trava o ciclo ────────────────
  const confirmarMontagem = async () => {
    if (!podeDefinir || escolhidas.size === 0 || gravando) return;
    setGravando(true);
    setAviso(null);
    const r = await post("/api/oc/carteira/selecao", { cnpjs: [...escolhidas] });
    if (!r.ok) {
      setAviso({ tipo: "erro", texto: explicar(r.corpo, r.http) });
    } else {
      const c = r.corpo && r.corpo.limite_empresas ? r.corpo : await recarregarCarteira();
      if (c) {
        setCarteira(c);
        if (onPronto) onPronto(c);
      }
      setEscolhidas(new Set());
      setAberto(true);
      setModo("incluir");
      setAviso({
        tipo: "ok",
        texto: `Carteira confirmada com ${plural(
          ((c && c.empresas) || []).length,
          "empresa",
          "empresas"
        )}. As vagas livres continuam abertas — incluir nelas não gasta troca.`,
      });
    }
    setGravando(false);
  };

  // ── gestão: simula antes de gravar ───────────────────────────────────
  const corpoDoPedido = () => ({
    remover: modo === "substituir" ? [...saindo] : [],
    incluir: [...entrando],
  });

  const simular = async () => {
    if (gravando) return;
    setGravando(true);
    setAviso(null);
    const r = await post("/api/oc/carteira/troca/simular", corpoDoPedido());
    if (!r.ok) {
      setPrevia(null);
      setAviso({ tipo: "erro", texto: explicar(r.corpo, r.http) });
    } else {
      setPrevia(r.corpo);
    }
    setGravando(false);
  };

  const aplicar = async () => {
    if (!podeDefinir || gravando || !previa) return;
    setGravando(true);
    setAviso(null);
    const r = await post("/api/oc/carteira/troca", corpoDoPedido());
    if (!r.ok) {
      setAviso({ tipo: "erro", texto: explicar(r.corpo, r.http) });
    } else {
      const nEntra = entrando.size;
      const nSai = saindo.size;
      setEntrando(new Set());
      setSaindo(new Set());
      setPrevia(null);
      const c = await recarregarCarteira();
      setAviso({
        tipo: "ok",
        texto:
          modo === "substituir"
            ? `Substituição gravada: ${plural(nSai, "saída", "saídas")} e ${plural(
                nEntra,
                "entrada",
                "entradas"
              )}. A janela de trocas desta vigência foi usada. A carteira agora tem ${
                ((c && c.empresas) || []).length
              }.`
            : `${plural(nEntra, "empresa incluída", "empresas incluídas")} nas vagas livres — sem gastar troca. A carteira agora tem ${
                ((c && c.empresas) || []).length
              }.`,
      });
    }
    setGravando(false);
  };

  // ── portões de renderização ──────────────────────────────────────────
  if (carregando) {
    return (
      <div style={{ fontFamily: FONT, padding: 40, color: C.muted, fontSize: 14 }}>
        Carregando o universo e o estado do seu contrato…
      </div>
    );
  }
  if (erroCarga) {
    return (
      <div style={{ fontFamily: FONT, padding: 40 }}>
        <div style={{ color: C.red, fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
          Não consegui carregar a seleção.
        </div>
        <div style={{ color: C.muted, fontSize: 13 }}>{erroCarga}</div>
      </div>
    );
  }

  /* Sem contrato de OC aberto, esta tela não tem o que fazer — e, pior, atrapalha:
     ela ficava por cima da carteira monitorada mostrando "0 de 0", como se o
     usuário tivesse um pacote vazio. Quem não tem pacote não deve ver a montagem;
     deve ver a tela que já existia. Some em silêncio e devolve o espaço. */
  const semContrato =
    !limite || (carteira && (carteira.status === "sem_ciclo" || !carteira.plan));
  if (semContrato) return null;

  const visiveis = filtradas.slice(0, mostrar);
  const marcados = jaConfirmada ? entrando : escolhidas;

  // ═══════════════════════════════════════════════════════════════════
  //  ESTADO 3 — confirmada: faixa fina, e o quadro só quando pedirem
  // ═══════════════════════════════════════════════════════════════════
  if (jaConfirmada && !aberto) {
    return (
      <div style={{ fontFamily: FONT, marginBottom: 16 }}>
        {aviso && <Nota cor={aviso.tipo === "erro" ? C.red : C.green}>{aviso.texto}</Nota>}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
            background: C.paper,
            border: `1px solid ${C.line}`,
            borderLeft: `3px solid ${C.amber}`,
            borderRadius: 10,
            padding: "12px 16px",
          }}
        >
          <div style={{ fontSize: 13, color: C.ink }}>
            <b>{monitoradas.size}</b> de <b>{limite}</b> empresas monitoradas
            {vagas > 0 && (
              <>
                {" · "}
                <b style={{ color: C.amber2 }}>{plural(vagas, "vaga livre", "vagas livres")}</b>
              </>
            )}
            {" · "}
            {janelaUsada
              ? "janela de trocas já usada"
              : `${plural(trocasRestantes, "troca disponível", "trocas disponíveis")}`}
          </div>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <Btn
              label={vagas > 0 ? `Incluir empresas (${vagas} vagas)` : "Gerir carteira"}
              tom={vagas > 0 ? "ouro" : "plano"}
              pequeno
              onClick={() => {
                setModo(vagas > 0 ? "incluir" : "substituir");
                setAberto(true);
              }}
              disabled={!podeDefinir}
              title={
                podeDefinir
                  ? undefined
                  : "Definir a carteira é de Admin, Admin CFO ou CFO/Diretor."
              }
            />
          </div>
        </div>
        {!podeDefinir && (
          <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6 }}>
            Quem inclui ou troca empresas é Admin, Admin CFO ou CFO/Diretor. Você segue com
            acesso de leitura à carteira.
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════
  //  ESTADO 2 (montagem) e ESTADO 3 aberto (gestão) — mesmo esqueleto
  // ═══════════════════════════════════════════════════════════════════
  const podeConfirmarMontagem = podeDefinir && escolhidas.size > 0 && !gravando;
  const pedidoValido =
    modo === "substituir"
      ? saindo.size > 0 || entrando.size > 0
      : entrando.size > 0;

  return (
    <div
      style={{
        fontFamily: FONT,
        color: C.ink,
        background: C.bg,
        minHeight: "100%",
        padding: "24px 20px 60px",
        borderRadius: 12,
        marginBottom: 18,
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Rotulo>RiskMind OC · Capital Aberto</Rotulo>
            <h1 style={{ fontSize: 28, fontWeight: 650, margin: "0 0 6px", letterSpacing: "-.01em" }}>
              {jaConfirmada ? "Gerir a carteira" : "Montar a carteira"}
            </h1>
            <p style={{ fontSize: 14, color: C.muted, margin: "0 0 20px", maxWidth: 760, lineHeight: 1.55 }}>
              {jaConfirmada ? (
                <>
                  Sua carteira tem <b style={{ color: C.ink }}>{monitoradas.size}</b> de{" "}
                  <b style={{ color: C.ink }}>{limite}</b> empresas. Preencher vaga livre{" "}
                  <b style={{ color: C.ink }}>não gasta troca</b> e pode ser feito quantas vezes
                  quiser. Substituir uma empresa que já está na carteira usa a{" "}
                  <b style={{ color: C.ink }}>janela única</b> da vigência.
                </>
              ) : (
                <>
                  Escolha até <b style={{ color: C.ink }}>{limite}</b> empresas entre as{" "}
                  <b style={{ color: C.ink }}>{universo.length}</b> de capital aberto que o RiskMind
                  acompanha. Você pode confirmar com menos: as vagas que sobrarem continuam suas e
                  podem ser preenchidas depois, sem gastar troca.
                </>
              )}
            </p>
          </div>
          {jaConfirmada && (
            <Btn label="Fechar" tom="plano" pequeno onClick={() => setAberto(false)} />
          )}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 22 }}>
          <Cartao k="Plano" v={(carteira && carteira.plan) || "—"} />
          <Cartao
            k={jaConfirmada ? "Monitoradas" : "Escolhidas"}
            v={`${jaConfirmada ? monitoradas.size : escolhidas.size} de ${limite}`}
          />
          {jaConfirmada && (
            <Cartao k="Vagas livres" v={vagas} tom={vagas > 0 ? C.amber2 : C.ink} />
          )}
          <Cartao
            k="Trocas na vigência"
            v={
              jaConfirmada
                ? janelaUsada
                  ? "janela usada"
                  : `${trocasRestantes} de ${(carteira && carteira.trocas_permitidas) || 0}`
                : (carteira && carteira.trocas_permitidas) || 0
            }
            tom={jaConfirmada && janelaUsada ? C.red : C.ink}
          />
          <Cartao k="Assentos" v={(carteira && carteira.assentos) || "—"} />
          <Cartao k="Vigência" v={`até ${fmtData(carteira && carteira.vigencia_fim)}`} />
        </div>

        {!podeDefinir && (
          <Nota cor={C.amber2}>
            Quem grava a carteira é Admin, Admin CFO ou CFO/Diretor. Você pode explorar o universo e
            montar uma sugestão aqui, mas quem confirma é um deles — a regra é do servidor, não
            desta tela.
          </Nota>
        )}
        {aviso && <Nota cor={aviso.tipo === "erro" ? C.red : C.green}>{aviso.texto}</Nota>}

        {/* ── seletor de modo (só depois de confirmada) ─────────────── */}
        {jaConfirmada && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            {[
              ["incluir", `Incluir em vaga livre (${vagas})`, vagas === 0, "Não há vaga livre no plano."],
              [
                "substituir",
                "Quadro de substituições",
                janelaUsada || trocasRestantes === 0,
                janelaUsada
                  ? "A janela de trocas desta vigência já foi usada."
                  : "Não há trocas contratadas disponíveis.",
              ],
            ].map(([id, rotulo, travado, porque]) => (
              <button
                key={id}
                onClick={() => {
                  if (travado) return;
                  setModo(id);
                  setPrevia(null);
                  setAviso(null);
                  setEntrando(new Set());
                  setSaindo(new Set());
                }}
                disabled={travado}
                title={travado ? porque : undefined}
                style={{
                  padding: "7px 14px",
                  borderRadius: 999,
                  cursor: travado ? "not-allowed" : "pointer",
                  fontSize: 13,
                  fontFamily: FONT,
                  fontWeight: modo === id ? 700 : 500,
                  border: "1px solid " + (modo === id ? C.navy : C.line),
                  background: modo === id ? C.navy : C.paper,
                  color: modo === id ? C.paper : C.muted,
                  opacity: travado ? 0.45 : 1,
                }}
              >
                {rotulo}
              </button>
            ))}
          </div>
        )}

        {jaConfirmada && modo === "substituir" && (
          <Nota cor={C.red}>
            <b>A janela de trocas abre uma vez só na vigência.</b> Marque de uma vez todas as
            saídas e entradas que pretende fazer neste ciclo — até{" "}
            {plural(trocasRestantes, "saída", "saídas")}. Depois de gravada, novas trocas só com a
            FinancialMind reabrindo a janela. Preencher vaga livre continua liberado sempre.
          </Nota>
        )}

        {/* ── quadro de substituições: quem sai ─────────────────────── */}
        {jaConfirmada && modo === "substituir" && (
          <div
            style={{
              background: C.paper,
              border: `1px solid ${C.line}`,
              borderRadius: 12,
              padding: 16,
              marginBottom: 16,
            }}
          >
            <Rotulo style={{ marginBottom: 10 }}>
              Quem sai · {saindo.size} de {trocasRestantes}
            </Rotulo>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
                gap: 8,
              }}
            >
              {[...monitoradas].map((k) => {
                const e = porCnpj.get(k) || {};
                const sai = saindo.has(k);
                const cheio = !sai && saindo.size >= trocasRestantes;
                return (
                  <label
                    key={k}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      padding: "8px 10px",
                      border: `1px solid ${sai ? C.red : C.line}`,
                      background: sai ? "#fef2f2" : C.paper,
                      borderRadius: 8,
                      cursor: cheio ? "not-allowed" : "pointer",
                      opacity: cheio ? 0.5 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={sai}
                      disabled={cheio}
                      onChange={() => marcarSaida(k)}
                      style={{ marginTop: 2 }}
                    />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 550, lineHeight: 1.3 }}>
                        {nomeDe(k)}
                      </div>
                      {/* A ÚNICA PD DESTA TELA, e ela é legítima: este quadro
                          lista quem JÁ ESTÁ na carteira do cliente — empresas
                          que ele contratou. O servidor manda a PD real para
                          essas linhas e nenhuma outra. Escolher quem sai sem
                          ver a PD de quem está dentro seria pedir a decisão
                          escondendo o dado que a fundamenta. */}
                      <div style={{ fontSize: 10.5, color: C.muted }}>
                        {e.rating || "—"} · PD {pct(e.pd, 2)} · {fmtCnpj(k)}
                      </div>
                    </div>
                  </label>
                );
              })}
              {monitoradas.size === 0 && (
                <div style={{ fontSize: 12, color: C.muted }}>Nenhuma empresa monitorada.</div>
              )}
            </div>
          </div>
        )}

        {/* ── filtros ───────────────────────────────────────────────── */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            alignItems: "center",
            background: C.paper,
            border: `1px solid ${C.line}`,
            borderRadius: 12,
            padding: 12,
            marginBottom: 16,
          }}
        >
          <input
            value={busca}
            onChange={(e) => {
              setBusca(e.target.value);
              setMostrar(60);
            }}
            placeholder="Buscar por nome, ticker ou CNPJ…"
            style={{ ...campoStyle, flex: "1 1 260px", minWidth: 220 }}
          />
          <select
            value={setor}
            onChange={(e) => {
              setSetor(e.target.value);
              setMostrar(60);
            }}
            style={{ ...campoStyle, flex: "0 1 280px" }}
          >
            <option value="">Todos os setores ({setores.length})</option>
            {setores.map((s) => (
              <option key={s.setor} value={s.setor}>
                {s.setor} ({s.n})
              </option>
            ))}
          </select>
          <select value={ordem} onChange={(e) => setOrdem(e.target.value)} style={{ ...campoStyle, flex: "0 1 210px" }}>
            <option value="rating_pior">Pior rating primeiro</option>
            <option value="rating_melhor">Melhor rating primeiro</option>
            <option value="nome">Nome (A–Z)</option>
            <option value="setor">Setor</option>
          </select>
          <label
            style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={soComMercado}
              onChange={(e) => setSoComMercado(e.target.checked)}
            />
            só com dado de mercado
          </label>
          <div style={{ marginLeft: "auto", fontSize: 12, color: C.muted }}>
            {filtradas.length} de {baseDeEscolha.length}
            {jaConfirmada ? " disponíveis" : ""}
          </div>
        </div>

        {/* ── lista + carrinho ──────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 620px", minWidth: 320 }}>
            <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, overflow: "hidden" }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.cream }}>
                      {/* SEM COLUNA DE PD — ver o cabeçalho do arquivo. */}
                      {["", "Empresa", "Setor", "Rating", "Percentil setor"].map((h, i) => (
                        <th
                          key={i}
                          style={{
                            textAlign: i >= 3 ? "right" : "left",
                            padding: "10px 12px",
                            fontSize: 11,
                            fontWeight: 600,
                            color: C.muted,
                            borderBottom: `1px solid ${C.line}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {visiveis.map((e) => {
                      const k = dig(e.cnpj);
                      const dentro = marcados.has(k);
                      const cheio = !dentro && marcados.size >= tetoEntrada;
                      return (
                        <tr
                          key={k}
                          style={{
                            borderBottom: `1px solid ${C.line}`,
                            background: dentro ? "#f0f7ff" : "transparent",
                            opacity: cheio ? 0.5 : 1,
                          }}
                        >
                          <td style={{ padding: "9px 12px", width: 34 }}>
                            <input
                              type="checkbox"
                              checked={dentro}
                              disabled={cheio}
                              onChange={() => marcarEntrada(k)}
                              style={{ cursor: cheio ? "not-allowed" : "pointer" }}
                            />
                          </td>
                          <td style={{ padding: "9px 12px" }}>
                            <div style={{ fontWeight: 550, lineHeight: 1.3 }}>{e.denom}</div>
                            <div style={{ fontSize: 11, color: C.muted }}>
                              {fmtCnpj(e.cnpj)}
                              {e.ticker ? ` · ${e.ticker}` : ""} · base {fmtData(e.data_base)}
                            </div>
                          </td>
                          <td style={{ padding: "9px 12px", fontSize: 12, color: C.muted, maxWidth: 200 }}>
                            {e.setor || "—"}
                          </td>
                          <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 600 }}>
                            {e.rating || "—"}
                          </td>
                          <td style={{ padding: "9px 12px", textAlign: "right", fontSize: 12, color: C.muted }}>
                            {fmtPercentil(e.sector_pct)}
                            {e.sector_peers ? ` · ${e.sector_peers} pares` : ""}
                          </td>
                        </tr>
                      );
                    })}
                    {visiveis.length === 0 && (
                      <tr>
                        <td colSpan={5} style={{ padding: 28, textAlign: "center", color: C.muted }}>
                          Nenhuma empresa com esses filtros.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filtradas.length > visiveis.length && (
                <div style={{ padding: 14, textAlign: "center", borderTop: `1px solid ${C.line}` }}>
                  <Btn
                    label={`Mostrar mais ${Math.min(60, filtradas.length - visiveis.length)}`}
                    tom="plano"
                    onClick={() => setMostrar((m) => m + 60)}
                  />
                </div>
              )}
            </div>
          </div>

          {/* carrinho */}
          <div style={{ flex: "0 1 330px", minWidth: 285, position: "sticky", top: 16 }}>
            <div style={{ background: C.paper, border: `1px solid ${C.line}`, borderRadius: 12, padding: 16 }}>
              <Rotulo style={{ marginBottom: 6 }}>
                {jaConfirmada ? (modo === "substituir" ? "Quem entra" : "Incluir agora") : "Sua carteira"}
              </Rotulo>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 4 }}>
                <div
                  style={{
                    fontSize: 30,
                    fontWeight: 700,
                    color: marcados.size >= tetoEntrada && tetoEntrada > 0 ? C.amber : C.ink,
                  }}
                >
                  {marcados.size}
                </div>
                <div style={{ fontSize: 14, color: C.muted }}>
                  {jaConfirmada ? `de ${tetoEntrada} possíveis` : `de ${limite}`}
                </div>
              </div>
              <div style={{ height: 6, background: C.line, borderRadius: 4, overflow: "hidden", marginBottom: 10 }}>
                <div
                  style={{
                    width: `${tetoEntrada ? Math.min(100, (marcados.size / tetoEntrada) * 100) : 0}%`,
                    height: "100%",
                    background: marcados.size >= tetoEntrada ? C.amber : C.blue,
                    transition: "width .2s",
                  }}
                />
              </div>

              <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
                {!jaConfirmada
                  ? limite - escolhidas.size > 0
                    ? `Ainda cabem ${limite - escolhidas.size}. Confirmar com menos é seguro: as vagas ficam suas e preenchê-las depois não gasta troca.`
                    : "Todos os slots ocupados."
                  : modo === "incluir"
                  ? `Entram nas ${plural(vagas, "vaga livre", "vagas livres")} do plano. Não consome a janela de trocas.`
                  : `Cada saída marcada abre uma vaga. Teto de entradas: ${tetoEntrada} (${vagas} livres + ${saindo.size} que saem).`}
              </div>

              <div style={{ maxHeight: 300, overflowY: "auto", marginBottom: 12 }}>
                {[...marcados].map((k) => {
                  const e = porCnpj.get(k) || {};
                  return (
                    <div
                      key={k}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 8,
                        padding: "7px 0",
                        borderBottom: `1px solid ${C.line}`,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 550,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {nomeDe(k)}
                        </div>
                        {/* Quem está no carrinho ainda NÃO é do cliente: o
                            servidor manda `pd: null` e isto imprimiria "PD —".
                            Rating e setor dizem o que o usuário precisa para
                            conferir que marcou a empresa certa. */}
                        <div style={{ fontSize: 10, color: C.muted }}>
                          {e.rating || "—"} · {e.setor || "—"}
                        </div>
                      </div>
                      <button
                        onClick={() => marcarEntrada(k)}
                        title="Tirar da lista"
                        style={{
                          border: "none",
                          background: "transparent",
                          color: C.muted,
                          cursor: "pointer",
                          fontSize: 16,
                          lineHeight: 1,
                          padding: "0 2px",
                        }}
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                {marcados.size === 0 && (
                  <div style={{ fontSize: 12, color: C.muted, padding: "10px 0" }}>
                    Nada marcado ainda. Escolha na lista ao lado.
                  </div>
                )}
              </div>

              {/* prévia do servidor antes de gravar */}
              {jaConfirmada && previa && (
                <div
                  style={{
                    background: previa.consome_janela ? "#fff7ed" : "#f0fdf4",
                    border: `1px solid ${previa.consome_janela ? "#fed7aa" : "#bbf7d0"}`,
                    borderRadius: 8,
                    padding: "10px 12px",
                    fontSize: 12,
                    lineHeight: 1.5,
                    marginBottom: 10,
                  }}
                >
                  <b>{previa.consome_janela ? "Usa a janela de trocas" : "Não usa a janela"}</b> ·
                  a carteira fica com <b>{previa.total_apos}</b> de {previa.limite_empresas}.
                  {previa.consome_janela
                    ? " Esta é a única troca da vigência: depois de gravar, novas trocas só com a FinancialMind reabrindo a janela."
                    : " Vagas livres podem ser preenchidas quantas vezes quiser."}
                </div>
              )}

              {!jaConfirmada ? (
                <>
                  <Btn
                    label={
                      gravando
                        ? "Confirmando…"
                        : `Confirmar ${plural(escolhidas.size, "empresa", "empresas")}`
                    }
                    tom="ouro"
                    onClick={confirmarMontagem}
                    disabled={!podeConfirmarMontagem}
                    style={{ width: "100%" }}
                  />
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                    A confirmação abre o ciclo. Depois dela, preencher vaga livre segue livre;
                    substituir uma empresa já monitorada usa a janela única de{" "}
                    {(carteira && carteira.trocas_permitidas) || 0} trocas da vigência.
                  </div>
                  {escolhidas.size > 0 && (
                    <div style={{ marginTop: 10 }}>
                      <Btn label="Limpar seleção" tom="perigo" pequeno onClick={() => setEscolhidas(new Set())} />
                    </div>
                  )}
                </>
              ) : (
                <>
                  {!previa ? (
                    <Btn
                      label={gravando ? "Conferindo…" : "Conferir antes de gravar"}
                      tom="principal"
                      onClick={simular}
                      disabled={!pedidoValido || gravando || !podeDefinir}
                      style={{ width: "100%" }}
                    />
                  ) : (
                    <Btn
                      label={
                        gravando
                          ? "Gravando…"
                          : modo === "substituir"
                          ? `Gravar ${plural(saindo.size, "substituição", "substituições")}`
                          : `Incluir ${plural(entrando.size, "empresa", "empresas")}`
                      }
                      tom="ouro"
                      onClick={aplicar}
                      disabled={gravando || !podeDefinir}
                      style={{ width: "100%" }}
                    />
                  )}
                  {(entrando.size > 0 || saindo.size > 0) && (
                    <div style={{ marginTop: 10 }}>
                      <Btn
                        label="Limpar marcações"
                        tom="perigo"
                        pequeno
                        onClick={() => {
                          setEntrando(new Set());
                          setSaindo(new Set());
                          setPrevia(null);
                          setAviso(null);
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
